import React, { useState, useEffect, useRef } from "react";
import { Stack, Typography } from "@mui/material";
import { GuestSelectInput, MeetingGuest } from "./inputs";
import { MeetingStatus, ContactWithDetails } from "../types";

interface GuestCellProps {
  meetingId: number;
  guest: MeetingGuest | null;
  meetingStatus: MeetingStatus;
  canAssignGuest: boolean;
  onGuestChange: (meetingId: number, newGuest: MeetingGuest) => Promise<void>;
}

export function GuestCell({
  meetingId,
  guest,
  meetingStatus,
  canAssignGuest,
  onGuestChange,
}: GuestCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isProvisioningCategory = (status: MeetingStatus): boolean => {
    return (
      status === "initializing" ||
      status === "initialized" ||
      status === "init_fault"
    );
  };

  const canEdit = canAssignGuest && isProvisioningCategory(meetingStatus);

  const handleGuestSelect = async (
    selectedGuest: ContactWithDetails | null,
  ) => {
    if (selectedGuest) {
      const meetingGuest: MeetingGuest = {
        id: selectedGuest.id,
        firstName: selectedGuest.firstName,
        lastName: selectedGuest.lastName,
        email: selectedGuest.email,
        unsubscribed: selectedGuest.unsubscribed,
      };
      await onGuestChange(meetingId, meetingGuest);
    }
    setIsEditing(false);
  };

  // Handle Escape key and click outside to close editing mode
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsEditing(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsEditing(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing]);

  if (isEditing) {
    return (
      <Stack
        ref={containerRef}
        sx={{ minWidth: 200 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <GuestSelectInput
          value={null}
          onGuestSelect={handleGuestSelect}
          label=""
        />
      </Stack>
    );
  }

  return (
    <Stack
      onClick={(e) => {
        if (canEdit) {
          e.stopPropagation();
          setIsEditing(true);
        }
      }}
      onMouseDown={(e) => {
        if (canEdit) {
          e.stopPropagation();
        }
      }}
      onKeyDown={(e) => {
        if (canEdit && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.stopPropagation();
          setIsEditing(true);
        }
      }}
      role={canEdit ? "button" : undefined}
      tabIndex={canEdit ? 0 : undefined}
      sx={{
        cursor: canEdit ? "pointer" : "default",
        padding: 1,
        borderRadius: canEdit ? 1 : 0,
        minHeight: 40,
        justifyContent: "center",
        "&:hover": canEdit ? { backgroundColor: "action.hover" } : {},
      }}
    >
      <Typography variant="caption">
        {guest?.firstName} {guest?.lastName}
      </Typography>
      <Typography variant="caption">{guest?.email}</Typography>
    </Stack>
  );
}
